import {Avatar} from 'antd'
import _ from 'lodash'
import getAvatar from 'helpers/getAvatar'
import {LoginContext} from 'components/LoginContext'
import useTranslate from 'modules/local/useTranslate'
import {useRouteSearchParam} from 'envs/_current/history'
import {UseHook} from 'components/UseReducer'
import React, {useContext} from 'react'
import {RiBarChart2Fill, RiUser3Fill, RiWechatLine} from 'react-icons/ri'
import {IoNewspaperOutline} from 'react-icons/io5'
import {branch, compose, fromRenderProps, renderNothing} from 'recompose'
import {getId} from 'apis/model/base'
import {useHistory} from 'modules/navigation/useRouter'
import ServerContainer from 'views/Server/ServerContainer'
import ServerContext from 'views/Server/ServerContext'
import EnhancedPost, {PostTypes} from 'views/AppLocals/factory/createPost'
import {FeedContentTypes} from 'views/Feed/Feed'
import {PostFormContext, PostFormProvider} from 'components/Feed/PostForm/PostFormContext'
import {permissionCodes} from 'modules/permissions/contants'
import PermissionContext from 'modules/permissions/PermissionContext'
import {LayoutContext} from "../../../../../components/layouts/Default/LayoutContext";

// Composer cho tab "Trao đổi" trong lớp học. Cùng khung PostFormProvider + ServerContainer +
// EnhancedPost(...) như GenericPostForm.js, nhưng chỉ có 4 lối tạo bài: post thường + Bình chọn +
// Bài viết + Hỏi đáp - KHÔNG có Ý nguyện (prayer) theo yêu cầu. UI trigger giữ riêng: avatar +
// input + hàng 3 icon inline (không có nút "Đăng bài" tách rời - bấm vào ô input = post thường).
const postRoutes = [
    {
        path: 'createPost',
        name: 'Create Post',
        component: EnhancedPost(PostTypes.DEFAULT),
        exact: true,
    },
    {
        path: 'createAsk',
        name: 'Create Q&A',
        component: EnhancedPost(PostTypes.ASK),
        exact: true,
    },
    {
        path: 'createPoll',
        name: 'Create Poll',
        component: EnhancedPost(PostTypes.POLL),
        exact: true,
    },
]

// 3 loại bổ sung - mirror POST_ACTIONS của GenericPostForm.js (cùng icon + i18n key + hành vi):
// Poll/Ask mở form ngay trong ServerContainer qua setCurrent; Bài viết điều hướng sang trang
// /article/new (kèm channel_id của kênh lớp). Giữ bản cục bộ, tự đủ - không tách module dùng chung.
const CLASS_POST_ACTIONS = [
    {
        type: FeedContentTypes.POLL,
        label: 'poll question',
        path: 'createPoll',
        icon: <RiBarChart2Fill size={20} className="text-green-500"/>,
    },
    {
        type: FeedContentTypes.ARTICLE,
        label: 'article',
        path: null,
        icon: <IoNewspaperOutline size={20} className="text-yellow-400"/>,
    },
    {
        type: FeedContentTypes.ASK,
        label: 'questions and answers',
        path: 'createAsk',
        icon: <RiWechatLine size={20} className="text-primary"/>,
    },
]

const PostInputTrigger = () => {
    const t = useTranslate()
    const {isLg} = useContext(LayoutContext)
    const login = useContext(LoginContext)
    const history = useHistory()
    const {setCurrent} = useContext(ServerContext)
    const {current} = useContext(PostFormContext)

    // Kênh đang chọn (PostFormProvider ghim sẵn kênh lớp) - dùng để gắn channel_id khi mở
    // trang tạo Bài viết, giống PostActions của GenericPostForm.js.
    const selectedChannel = current?.container ?? null

    const handleAction = ({type, path}) => {
        if (type === FeedContentTypes.ARTICLE) {
            const channelId = getId(selectedChannel)
            history.push(
                channelId ? `/article/new?channel_id=${channelId}` : '/article/new'
            )
            return
        }
        if (path) setCurrent(path)
    }

    return (
        <div
            className="flex items-center gap-2 p-2 background border border-color-50 shadow-items-md rounded-2xl overflow-hidden">
            <Avatar
                size={32}
                src={getAvatar(login)}
                icon={<RiUser3Fill className="text-color-400"/>}
                className="flex flex-center background-200 rounded-full flex-shrink-0"
            />
            <div
                onClick={() => setCurrent('createPost')}
                style={{height: 40}}
                className="flex items-center flex-1 min-w-0 background-200 hover:background-100 rounded-full cursor-pointer px-4 transition-colors">
                <span className="text-sm text-color-400 italic leading-tight truncate">
                    {t('share your thoughts')}
                </span>
            </div>
            {/* Hàng 3 icon inline thay cho nút "Đăng bài" cũ - bấm vào ô input phía trên = post
                thường. Chỉ hiện icon, nhãn đưa vào title để hover/accessibility. */}
            <div className="flex items-center gap-1 flex-shrink-0">
                {CLASS_POST_ACTIONS.map((action, index) => (
                    <button
                        key={index}
                        type="button"
                        title={t(action.label)}
                        onClick={() => handleAction(action)}
                        style={{width: isLg ? 36 : 30, height: isLg ? 36 : 30, cursor: 'pointer'}}
                        className="flex flex-center rounded-lg text-color-300 hover:text-secondary-600 hover:background-200 transition-colors">
                        {action.icon}
                    </button>
                ))}
            </div>
        </div>
    )
}

const CourseClassPostInputBar = ({container, defaultChannel}) => {
    const login = useContext(LoginContext)
    if (!login) return null

    return (
        <PostFormProvider container={container} defaultChannel={defaultChannel}>
            <UseHook hook={useRouteSearchParam} deps={['initialCurrent']}>
                {(initialCurrent) => (
                    <ServerContainer
                        routes={postRoutes}
                        initialCurrent={initialCurrent}>
                        <PostInputTrigger/>
                    </ServerContainer>
                )}
            </UseHook>
        </PostFormProvider>
    )
}

// Cùng gate quyền `canPost` mà GenericPostForm.js dùng (đọc qua PermissionProvider đã bọc sẵn ở
// CourseClassNews.js) - đổi từ GenericPostForm sang component này KHÔNG được làm mất quyền kiểm tra
// đó, nếu không mọi học viên (kể cả không có quyền đăng) đều thấy được composer.
export default compose(
    fromRenderProps(PermissionContext.Consumer, (props) => props),
    branch(
        (props) => !Boolean(_.get(props, permissionCodes.canPost)),
        renderNothing
    )
)(CourseClassPostInputBar)
